import { API } from "@aws-amplify/api"
import { stripB64Extra, getBase64 } from "../s3Lib/s3Lib"
import type { App, CustomOutput, EmbedCardDataset, UserFromDb } from "../../DatabaseObjects/DatabaseObjects"
import { cleanObject } from "../objLib/objLib"
import type { TelemetryEvent } from "../../types/telemetry.types"
import type {
	MigrationInitiateRequest,
	MigrationInitiateResponse,
	MigrationStatusRequest,
	MigrationStatusResponse,
	MigrationJobsRequest,
	MigrationJobsResponse,
} from "../../types/migration.types"
import type { Method, QSParams, MVQSParams } from "./apiLibUtils"
import {
	createSecurityParameters,
	makeApiEndpointSet,
	indexArray,
	getCognitoToken,
	addIdsToPath,
	getTTCache,
	combinePreviousText,
	makeUrl,
	addIdsToFreeText,
	addFreeTextIdsToTemplating,
} from "./apiLibUtils"

import type {
	FetchedFileObject,
	FileFetchingDetails,
	FileListObject,
	IntegrationData,
} from "../../components/Integrations/Integrations.types"

interface ApiError extends Error {
	response?: {
		status: number
		data?: {
			message?: string
		}
	}
}

export const applicationApi = makeApiEndpointSet("/applications", {
	get: {
		type: "get",
		idForPath: "folderId",
		mapping: {
			queryStringParameters: {
				processedOnly: "processedOnly",
				excludeDuplicates: "excludeDuplicates",
				attributes: "attrList",
			},
		},
		defaultValues: {
			processedOnly: true,
			attrList: [
				"totalExperience",
				"averageTenure",
				"score",
				"fileTypes",
				"candidateName",
				"emailAddress",
				"phoneNumber",
				"identifier",
				"linkedUrl",
				"template",
				"coverLetter",
				"templateVersion",
			],
		},
		outputTransform: (output) => indexArray(output, "application_id"),
		errorHandler: (error) => {
			console.log(error)
			if (error.response?.status === 404) {
				console.log("404 received - assume no applications exist")
				return {}
			}
			throw error
		},
	},
	getByExternalId: {
		type: "get",
		idForPath: "folderId",
		mapping: {
			queryStringParameters: {
				platform: "platform",
				externalClientId: "externalClientId",
				externalAppId: "externalAppId",
				byExternalId: "byExternalId",
				attributes: "attrList",
			},
		},
		defaultValues: {
			byExternalId: true,
			attrList: ["candidateName", "identifier", "template", "submittedBy", "receivedTime", "outputName"],
		},
		errorHandler: (error) => {
			console.log(error)
			if (error.response?.status === 404) {
				console.log("404 received - assume no applications exist")
				return {}
			}
			throw error
		},
	},
	getFamily: {
		type: "get",
		idForPath: "folderId",
		mapping: {
			queryStringParameters: {
				byFamily: "byFamily",
				familyId: "familyId",
				attributes: "attrList",
			},
		},
		defaultValues: {
			byFamily: true,
		},
		errorHandler: (error) => {
			if (error.response?.status === 404) return []
			throw error
		},
	},
	add: {
		type: "post",
		inputTransform: cleanObject,
		idForPath: "folderId",
		mapping: {
			queryStringParameters: {
				cvType: "fileType",
				submittedBy: "submittedBy",
				defaultTemplate: "defaultTemplate",
				platform: "platform",
				externalClientId: "externalClientId",
				externalId: "externalId",
				externalUserId: "externalUserId",
				jobId: "jobId",
			},
			body: {
				fileB64: "fileB64",
				files: "files",
				supportingDocuments: "supportingDocuments",
				integrationFilesToFetch: "integrationFilesToFetch",
			},
			defaultValues: {
				fileB64: undefined,
				files: undefined,
				fileType: "cv",
			},
		},
	},
	updateStatus: {
		type: "put",
		idForPath: "appId",
		mapping: {
			queryStringParameters: {
				updateType: "newStatus",
			},
		},
	},
	updateCandidateHeadshot: {
		type: "put",
		idForPath: "appId",
		mapping: {
			queryStringParameters: {
				updateType: "updateType",
			},
			body: {
				headshot: "headshot",
				fileType: "fileType",
			},
		},
		defaultValues: {
			updateType: "update_candidate_headshot",
		},
	},
	updateEmbed: {
		type: "put",
		idForPath: "appId",
		mapping: {
			queryStringParameters: {
				updateType: "updateType",
			},
			body: {
				url: "url",
				embedCardId: "embedCardId",
				width: "width",
			},
		},
		defaultValues: {
			updateType: "embed",
		},
		outputTransform: addFreeTextIdsToTemplating,
	},
	runAiParser: {
		type: "put",
		idForPath: "appId",
		mapping: {
			queryStringParameters: {
				updateType: "updateType",
			},
		},
		defaultValues: {
			updateType: "aiParse",
		},
		outputTransform: addFreeTextIdsToTemplating,
	},
	transform: {
		type: "put",
		idForPath: "appId",
		mapping: {
			queryStringParameters: {
				updateType: "updateType",
			},
			body: {
				transformType: "transformType",
				transformOptions: "transformOptions",
			},
		},
		defaultValues: {
			updateType: "transform",
		},
		outputTransform: addFreeTextIdsToTemplating,
	},
	duplicate: {
		type: "put",
		idForPath: "appId",
		mapping: {
			queryStringParameters: {
				updateType: "updateType",
				newOwner: "newOwner",
			},
		},
		defaultValues: {
			updateType: "duplicate_cv",
		},
		outputTransform: addFreeTextIdsToTemplating,
	},
	createContent: {
		type: "put",
		idForPath: "appId",
		mapping: {
			queryStringParameters: {
				updateType: "updateType",
				newOwner: "newOwner",
			},
			body: {
				supportingDocuments: "supportingDocuments",
				defaultTemplate: "defaultTemplate",
			},
		},
		defaultValues: {
			updateType: "create_content",
		},
		outputTransform: addFreeTextIdsToTemplating,
	},
	generateTags: {
		type: "put",
		idForPath: "appId",
		mapping: {
			queryStringParameters: {
				updateType: "updateType",
				tags: "tags",
			},
			body: {
				updates: "updates",
			},
		},
		defaultValues: {
			updateType: "generate_tags",
		},
		outputTransform: addFreeTextIdsToTemplating,
	},
	applyTemplate: {
		type: "put",
		idForPath: "appId",
		mapping: {
			queryStringParameters: {
				updateType: "updateType",
				templateId: "templateId",
			},
		},
		defaultValues: {
			updateType: "apply_template",
		},
		outputTransform: addFreeTextIdsToTemplating,
	},
	patch: {
		type: "patch",
		idForPath: "appId",
		mapping: {
			body: "update",
		},
		outputTransform: (output) => true,
		errorHandler: (error) => {
			if (error.response?.status === 405) {
				alert("This CV has been removed from verification")
				return false
			}
		},
		inputTransform: (input) => {
			// remove embedCode from all embedCard objects
			return cleanObject({
				...input,
				body: {
					...input.body,
					embedCard: removeEmbedCodeFromEmbedCard(input.body.embedCard),
				},
			})
		},
	},
	delete: {
		type: "del",
		idForPath: "appId",
		condition: (props) => {
			if (props.confirmFirst === false) {
				return false
			}
			const message = `Are you sure you want to delete this CV?`
			// eslint-disable-next-line no-restricted-globals
			if (confirm(message) === false) {
				throw new Error("Delete canceled")
			}
			return true
		},
		mapping: {},
		defaultValues: {
			confirmFirst: true,
		},
	},
	getSingle: {
		type: "get",
		idForPath: "appId",
		mapping: {
			queryStringParameters: {
				attributes: "attributes",
			},
		},
		inputTransform: (input) => {
			if (input.queryStringParameters.attributes === undefined) {
				delete input.queryStringParameters.attributes
				return input
			}
			return input
		},
		outputTransform: (output) => {
			const transformedOutput = {
				...output[0],
				loadedIndividually: true,
				freeText: addIdsToFreeText(output[0].freeText),
			}
			return transformedOutput
		},
	},
})

export async function adminGetClients({
	attrList = [
		"stats",
		"credits",
		"paymentType",
		"options",
		"billingDate",
		"manualTracking",
		"evoRef",
		"cuCode",
		"evoOrgId",
	],
}) {
	const headers = await getCognitoToken()
	const params: QSParams | MVQSParams = {}
	if (attrList !== undefined) {
		params.attributes = attrList
	}
	try {
		const record = indexArray(
			await API.get(global.config.apiGateway.NAME, "/allclients", {
				queryStringParameters: params,
				headers,
			}),
			"client_id"
		)
		return record
	} catch (error) {
		if (error.response?.status === 404) {
			console.log("404 received - assume no clients exist")
			return {}
		}
		throw error
	}
}

export const clientApi = makeApiEndpointSet("/clients", {
	create: {
		type: "post",
		mapping: {
			queryStringParameters: {
				clientName: "company",
				clientSubdomain: "subdomain",
			},
		},
		defaultValues: {
			numberRoles: -1,
			presentRole: false,
		},
		shouldUseSecurity: false,
	},
	get: {
		type: "get",
		mapping: {
			queryStringParameters: {
				query: "query",
			},
		},
		errorHandler: async (error: ApiError) => {
			if (error.response?.status === 423) {
				// log out and display error message user if account is disabled
				throw new Error(
					error.response?.data?.message?.replace("Error:", "") ?? "Access to this account has been disabled."
				)
			} else {
				throw error
			}
		},
		inputTransform: (input) => ({
			...input,
			queryStringParameters: {
				...input.queryStringParameters,
				...input.queryStringParameters.query,
			},
		}),
	},
	update: {
		type: "put",
		mapping: {
			queryStringParameters: {
				clientName: "clientName",
				updateType: "updateType",
				userId: "userId",
			},
		},
	},
	addCustomTag: {
		type: "put",
		mapping: {
			body: { tag: "tag", tagName: "tagName", tagType: "tagType" },
			queryStringParameters: {
				updateType: "updateType",
			},
		},
		defaultValues: {
			updateType: "customTag",
		},
	},
	setSSOEnabled: {
		type: "put",
		mapping: {
			body: { ssoEnabled: true },
			queryStringParameters: {
				updateType: "updateType",
			},
		},
		defaultValues: {
			updateType: "enableSSO",
		},
	},
	saveIntegrationFieldMappings: {
		type: "put",
		mapping: {
			body: { field_mappings: "field_mappings", platform: "platform", export_types: "export_types" },
			queryStringParameters: {
				updateType: "updateType",
			},
		},
		defaultValues: {
			updateType: "integrationFieldMappings",
		},
	},
	updateLanguage: {
		type: "put",
		mapping: {
			body: { language: "language" },
			queryStringParameters: {
				updateType: "updateType",
			},
		},
		defaultValues: {
			updateType: "language",
		},
	},
	updateSubdomain: {
		type: "put",
		mapping: {
			body: { subdomain: "subdomain", updateType: "updateType" },
		},
		defaultValues: {
			updateType: "subdomain",
		},
	},
	patch: {
		type: "patch",
		mapping: {
			body: "updates",
			queryStringParameters: {
				changesOnly: "changesOnly",
			},
		},
		outputTransform: (output) => true,
		errorHandler: (error) => {
			console.log("error", error)
			return false
		},
	},
})

export const customOutputApi = makeApiEndpointSet("/output", {
	post: {
		type: "post",
		idForPath: "appId",
		mapping: {
			body: {
				outputName: "outputName",
				fileName: "fileName",
				userId: "userId",
				chapters: "chapters",
				bullhornOutputData: "bullhornOutputData",
				pdfB64: "pdfB64",
				imageB64: "imageB64",
				imageType: "imageType",
				imageFileType: "imageFileType",
			},
		},
	},
})

export const linksApi = makeApiEndpointSet("/links", {
	get: {
		type: "get",
		idForPath: "appId",
		mapping: {
			queryStringParameters: {
				attributes: "attributes",
			},
		},
	},
	getSingle: {
		type: "get",
		idForPath: "linkId",
		mapping: {
			queryStringParameters: {
				attributes: "attributes",
			},
		},
		outputTransform: (output) => output[0],
	},
	add: {
		type: "post",
		idForPath: "appId",
		mapping: {
			queryStringParameters: {
				name: "name",
				expiry: "expiry",
				updateEmails: "updateEmails",
				downloading: "downloading",
			},
		},
	},
	patch: {
		type: "patch",
		idForPath: "linkId",
		mapping: {
			body: "body",
		},
	},
	updateLinks: {
		type: "put",
		idForPath: "linkId",
		mapping: {
			queryStringParameters: {
				updateType: "updateType",
				newExpiry: "newExpiry",
			},
		},
		defaultValues: {
			updateType: "expiry",
		},
	},
})

export const viewsApi = makeApiEndpointSet("/views", {
	add: {
		type: "post",
		idForPath: "sessionId",
		mapping: {
			queryStringParameters: {},
			body: {
				buttonId: "buttonId",
				downloaded: "downloaded",
			},
		},
	},
})

export const adminAccessApi = makeApiEndpointSet("/access", {
	get: {
		type: "get",
		mapping: {
			queryStringParameters: {
				accessType: "accessType",
				userType: "userType",
				singleApplication: "singleApplication",
				singleTemplate: "singleTemplate",
			},
		},
	},
})

export async function updateFolder({ body }) {
	const secParams = await createSecurityParameters({})
	const path = "/folders"
	const fullPath = addIdsToPath(path, body.folderId)
	const newFolder = await API.put(global.config.apiGateway.NAME, fullPath, {
		headers: { "Content-Type": "application/json", ...secParams.headers },
		body: body,
	})
	return newFolder
}

export async function getEvoButtonToken({ clientId }: { clientId: string }) {
	const secParams = await createSecurityParameters({})
	const path = "/evo/button/token"
	const fullPath = addIdsToPath(path, clientId)
	const baseUrl: string = global.config.apiGateway.URL
	// use fetch api directly as full unhandled response needed
	const response = await fetch(baseUrl + fullPath, {
		headers: { "Content-Type": "application/json", ...secParams.headers },
	})
	return response
}

export async function createFolder({ qsParams, body }: { qsParams?: object; body: { clientId: string } }) {
	const secParams = await createSecurityParameters({})

	const path = "/folders"
	const fullPath = addIdsToPath(path, body.clientId)
	try {
		const folder = await API.post(global.config.apiGateway.NAME, fullPath, {
			headers: { "Content-Type": "application/json", ...secParams.headers },
			queryStringParameters: qsParams ?? {},
			body: body,
		})
		return folder
	} catch (e) {
		if (e.response?.status === 403) {
			throw new Error("No credit available to create new folder, please contact account manager")
		} else {
			throw e
		}
	}
}

export async function getTemplateThumbnail(clientId: string, templateId: string) {
	const secParams = await createSecurityParameters({})

	const idParts = templateId.split("_")
	const subId1 = idParts[1]
	const fullPath = `/${clientId}/templates/${subId1}/thumbnails`

	try {
		const response = await API.get(global.config.apiGateway.NAME, fullPath, {
			headers: secParams.headers,
		})
		return response
	} catch (error) {
		return null
	}
}

export async function getFolders(companyId) {
	const secParams = await createSecurityParameters({})
	let foldersObj

	const path = "/folders"
	const fullPath = addIdsToPath(path, companyId)
	try {
		const folders = await API.get(global.config.apiGateway.NAME, fullPath, {
			headers: secParams.headers,
		})
		foldersObj = indexArray(folders, "folder_id")
	} catch (error) {
		if (error.response?.status === 404) {
			console.log("404 received - assume no folders exist")
			foldersObj = {}
		} else {
			throw error
		}
	}

	return foldersObj
}

export async function createJob({ qsParams, body }: { qsParams?: object; body }) {
	const secParams = await createSecurityParameters({})
	const path = "/jobs"
	const fullPath = addIdsToPath(path, body.clientId)
	try {
		const job = await API.post(global.config.apiGateway.NAME, fullPath, {
			headers: { "Content-Type": "application/json", ...secParams.headers },
			queryStringParameters: qsParams ?? {},
			body: body,
		})
		return job
	} catch (e) {
		if (e.response?.status === 403) {
			throw new Error("No credit available to create new job, please contact account manager")
		} else {
			throw e
		}
	}
}

export async function getJobs(clientId, jobId) {
	const secParams = await createSecurityParameters({})
	let jobsObj
	const path = "/jobs"
	const fullPath = addIdsToPath(path, jobId ?? clientId)
	try {
		const jobs = await API.get(global.config.apiGateway.NAME, fullPath, {
			headers: secParams.headers,
		})
		jobsObj = indexArray(jobs, "job_id")
	} catch (error) {
		if (error.response?.status === 404) {
			console.log("404 received - assume no jobs exist")
			jobsObj = {}
		} else {
			throw error
		}
	}

	return jobsObj
}

export async function patchJob(updates) {
	const secParams = await createSecurityParameters({})
	const path = "/jobs"
	const fullPath = addIdsToPath(path, updates.jobId)
	const job = await API.patch(global.config.apiGateway.NAME, fullPath, {
		body: updates,
		headers: secParams.headers,
	})

	return job
}

export async function updateJob({ jobId, newStatus }: { jobId: string; newStatus: "open" | "closed" }) {
	const secParams = await createSecurityParameters({})
	const path = "/jobs"
	const fullPath = addIdsToPath(path, jobId)
	const job = await API.put(global.config.apiGateway.NAME, fullPath, {
		queryStringParameters: { jobId, newStatus },
		headers: secParams.headers,
	})

	return job
}

export const filesApi = makeApiEndpointSet("/files", {
	get: {
		type: "get",
		idForPath: "appId",
		mapping: {},
		errorHandler: (error) => {
			return {}
		},
	},
	add: {
		type: "post",
		idForPath: "appId",
		mapping: {
			body: { fileB64: "fileB64", fileType: "fileType", designation: "designation" },
		},
	},
})
export const getImageTemplateFiles = async ({
	companyId,
	imageTemplateId,
	templateId,
}: {
	companyId: string
	imageTemplateId: string
	templateId: string
}) => {
	const secParams = await createSecurityParameters({})
	const idParts = templateId.split("_")
	const subId1 = idParts[1]
	const fullPath = `/${companyId}/${subId1}/imageTemplates/${imageTemplateId}/files`
	const imageTemplateFiles = await API.get(global.config.apiGateway.NAME, fullPath, {
		headers: secParams.headers,
	})
	return imageTemplateFiles
}

export async function createUser({
	userEmail,
	userName,
	userRole,
	companyId,
	company,
	userType = "app",
	platform,
	externalClientId,
	externalUserId,
}: {
	userEmail: string
	userName: string
	userRole: string
	companyId?: string
	company: string
	userType?: string
	platform?: string
	externalClientId?: string
	externalUserId?: string
}) {
	const secParams = await createSecurityParameters({})
	const queryStringParameters: {
		email: string
		name: string
		userRole: string
		userType: string
		company_ID?: string
		company?: string
		platform?: string
		externalClientId?: string
		externalUserId?: string
	} = cleanObject(
		{
			email: userEmail,
			name: userName,
			userRole: userRole,
			userType: userType,
			platform: platform,
			externalClientId: externalClientId,
			externalUserId: externalUserId,
		},
		true
	)
	if (company != null) {
		queryStringParameters.company = company
	}
	const fullPath = addIdsToPath("/users", companyId)

	const clientId = await API.post(global.config.apiGateway.NAME, fullPath, {
		queryStringParameters,
		headers: secParams.headers,
	})
	return clientId
}

export async function getUsers(body: {
	clientId: string
	userId?: string
	attributes?: string[]
	externalUserId?: string
	externalClientId?: string
	platform?: string
	email?: string
}) {
	const secParams = await createSecurityParameters({})

	const { clientId, ...otherBody } = body
	const params = cleanObject(otherBody, true)
	const path = "/users"
	const fullPath = addIdsToPath(path, clientId)
	const response = await API.get(global.config.apiGateway.NAME, fullPath, {
		queryStringParameters: params,
		headers: secParams.headers,
	})
	if (body.userId != null || body.externalUserId != null) {
		return response[0]
	}
	return response
}

export async function getMigrationUrl(clientId: string, params: { userId: string }) {
	const secParams = await createSecurityParameters({})

	const cleanedParams = cleanObject(params, true)
	const path = "/evo-migration-url"
	const fullPath = addIdsToPath(path, clientId)
	const response = await API.get(global.config.apiGateway.NAME, fullPath, {
		queryStringParameters: cleanedParams,
		headers: secParams.headers,
	})
	return response
}

export async function getSSORedirectLink(body: { clientId: string; userId?: string }) {
	const secParams = await createSecurityParameters({})

	const { clientId, ...otherBody } = body
	const params = cleanObject(otherBody, true)
	const path = "/sso_redirect_link"
	const fullPath = addIdsToPath(path, clientId)
	const response = await API.get(global.config.apiGateway.NAME, fullPath, {
		queryStringParameters: params,
		headers: secParams.headers,
	})

	return response
}

export async function updateUser({ email, userId, name, userRole, clientId, accessIdToken }) {
	const path = "/users"
	const fullPath = addIdsToPath(path, clientId)
	const secParams = await createSecurityParameters({})
	const body: any = { email, userId }
	if (name !== undefined) {
		body.name = name
	}
	if (userRole !== undefined) {
		body.userRole = userRole
	}
	if (accessIdToken !== undefined) {
		body.accessIdToken = accessIdToken
	}
	try {
		await API.put(global.config.apiGateway.NAME, fullPath, {
			body,
			headers: secParams.headers,
		})
	} catch {
		console.log("failed to update user")
	}
}

export async function updateSignature(body: { clientId; email: string; name: string }) {
	const secParams = await createSecurityParameters({})
	const path = "/signatures"
	const fullPath = addIdsToPath(path, body.clientId)
	try {
		const response = await API.put(global.config.apiGateway.NAME, fullPath, {
			body,
			headers: secParams.headers,
		})
		return response
	} catch {
		console.log("failed to update signature")
	}
}

type PatchUserParams = {
	email: string
	name?: string
	userRole?: string
	clientId: string
	clientName?: string
	phone?: string
	jobTitle?: string
	linkedInUrl?: string
	userDescription?: string
	displayName?: string
	displayCompanyName?: string
	calendarLinkUrl?: string
} & ({ userId: string } | { platform: string; externalUserId: string; externalClientId })

export async function patchUser(inputBody: PatchUserParams): Promise<UserFromDb> {
	const secParams = await createSecurityParameters({})
	const { clientId, ...otherBody } = inputBody
	const body = cleanObject(otherBody)
	const path = "/users"
	const fullPath = addIdsToPath(path, clientId)

	try {
		return await API.patch(global.config.apiGateway.NAME, fullPath, {
			body,
			headers: secParams.headers,
		})
	} catch {
		console.log("failed to patch user")
	}
}

export async function deleteUser({ userId, companyId, company, userEmail }) {
	const secParams = await createSecurityParameters({})
	const path = "/users"
	const fullPath = addIdsToPath(path, companyId)
	// try {
	await API.del(global.config.apiGateway.NAME, fullPath, {
		queryStringParameters: {
			userId: userId,
			company: company,
			companyId: companyId,
			EMAIL: userEmail,
		},
		headers: secParams.headers,
	})
	// } catch (e) {
	//   alert('Failed to delete user',e)
	// }
	return
}

export async function addLogo({ b64File, clientId, fileType, fileName, setAsCurrent = true, cache = true }) {
	const secParams = await createSecurityParameters({})
	const strippedB64 = stripB64Extra(b64File)
	const path = "/logos"
	const fullPath = addIdsToPath(path, clientId)
	try {
		const response = await API.post(global.config.apiGateway.NAME, fullPath, {
			queryStringParameters: {
				fileType: fileType,
				fileName: fileName,
				setAsCurrent: setAsCurrent,
			},
			body: strippedB64,
			headers: secParams.headers,
		})
		if (cache && window.caches != null) {
			window.caches.open("logoCache").then((cache) => {
				"add to cache for get call on upload, async"
				const qsParams = {
					fileName: response,
				}
				const requestParams = {
					queryStringParameters: qsParams,
					headers: secParams.headers,
				}
				const cacheRequest = new Request(makeUrl(fullPath, qsParams), {
					...requestParams,
					method: "GET",
				})

				cache.put(cacheRequest, new Response(b64File))
			})
		}
		return response
	} catch (e) {
		alert(`image file not accepted:\n ${e.response?.data.message ?? e.message}`)
		throw new Error(e)
	}
}

export async function getLogo({ clientId, fileName }) {
	const secParams = await createSecurityParameters({})
	const path = "/logos"
	const fullPath = addIdsToPath(path, clientId)
	const qsParams: QSParams = {}
	const requestParams = {
		queryStringParameters: qsParams,
		headers: secParams.headers,
	}
	let cacheRequest: Request | null = null
	let cache
	if (fileName != null) {
		qsParams.fileName = fileName
	}

	if (fileName != null && window.caches != null) {
		cache = await window.caches.open("logoCache")
		cacheRequest = new Request(makeUrl(fullPath, qsParams), {
			...requestParams,
			method: "GET",
		})
		const cachedResponse = await cache.match(cacheRequest)
		if (cachedResponse) {
			return await cachedResponse.text()
		}
	}
	try {
		const response = await API.get(global.config.apiGateway.NAME, fullPath, requestParams)
		if (cacheRequest != null) {
			cache.put(cacheRequest, new Response(response))
		}
		return response
	} catch (error) {
		if (error.response?.status === 404) {
			console.log("404 received assume no logo exists")
			return null
		}
		throw error
	}
}
const removeEmbedCodeFromEmbedCard = (embedCard: {
	[embedId: string]: EmbedCardDataset
}): { [embedId: string]: EmbedCardDataset } => {
	if (embedCard == null || Object.keys(embedCard).length === 0) return undefined
	return Object.fromEntries(
		Object.entries(embedCard).map(([key, value]: [string, EmbedCardDataset]) => [
			key,
			{ ...value, embedCode: undefined },
		])
	)
}

export const templateApi = makeApiEndpointSet("/templates", {
	create: {
		type: "post",
		mapping: {
			queryStringParameters: {
				createdBy: "userId",
				templateVersion: "templateVersion",
				copyFrom: "copyFrom",
			},
		},
		defaultValues: {
			templateVersion: null,
		},
		inputTransform: cleanObject,
		errorHandler: (e) => {
			console.log(e)
			console.log(e.message)
		},
	},
	createFromDefault: {
		type: "post",
		mapping: {
			queryStringParameters: {
				defaultTemplateId: "defaultTemplateId",
			},
		},
	},
	get: {
		type: "get",
		mapping: {
			queryStringParameters: {
				attributes: "attributes",
			},
		},
		outputTransform: (response) => {
			return indexArray(response, "template_id")
		},
		errorHandler: (error) => {
			if (error.response?.status === 404) {
				console.log("404 received - assume no templates exist")
				return {}
			} else {
				throw error
			}
		},
	},
	getSingle: {
		type: "get",
		idForPath: "templateId",
		mapping: {
			queryStringParameters: {
				attributes: "attributes",
			},
		},
		outputTransform: (output) => {
			const transformedOutput = {
				...output,
				loadedIndividually: true,
				freeText: addIdsToFreeText(output.freeText),
			}
			return transformedOutput
		},
	},
	patch: {
		type: "patch",
		idForPath: "templateId",
		mapping: {
			body: "body",
			queryStringParameters: {
				changesOnly: "changesOnly",
			},
		},
		inputTransform: (input) => {
			// remove embedCode from all embedCard objects
			return cleanObject({
				...input,
				body: {
					...input.body,
					embedCard: removeEmbedCodeFromEmbedCard(input.body.embedCard),
				},
			})
		},
		outputTransform: addFreeTextIdsToTemplating,
	},
	updateLiveStatus: {
		type: "put",
		idForPath: "templateId",
		mapping: {
			body: "body",
			queryStringParameters: {
				updateType: "updateType",
			},
		},
		defaultValues: {
			updateType: "liveStatus",
		},
		outputTransform: addFreeTextIdsToTemplating,
	},
	updateEmbed: {
		type: "put",
		idForPath: "templateId",
		mapping: {
			queryStringParameters: {
				updateType: "updateType",
			},
			body: {
				url: "url",
				templateUrl: "templateUrl",
				embedCardId: "embedCardId",
				contentChosen: "contentChosen",
				type: "type",
				fixedSource: "fixedSource",
				width: "width",
			},
		},
		defaultValues: {
			updateType: "embed",
		},
		inputTransform: (input) => {
			// remove embedCode from all embedCard objects
			return cleanObject({
				...input,
				body: {
					...input.body,
					embedCard: removeEmbedCodeFromEmbedCard(input.body.embedCard),
				},
			})
		},
		outputTransform: addFreeTextIdsToTemplating,
	},
	updateImageTemplate: {
		type: "put",
		idForPath: "templateId",
		mapping: {
			queryStringParameters: {
				updateType: "updateType",
			},
			body: {
				imageTemplate: "imageTemplate",
				fileType: "fileType",
				imageTemplateId: "imageTemplateId",
				width: "width",
				height: "height",
			},
		},
		defaultValues: {
			updateType: "update_image_template",
		},
	},
	delete: {
		type: "del",
		idForPath: "templateId",
		mapping: {},
		outputTransform: (response) => {
			return
		},
	},
})

export const defaultTemplateApi = makeApiEndpointSet("/defaulttemplates", {
	get: {
		type: "get",
		mapping: {
			queryStringParameters: {
				defaultTemplateId: "defaultTemplateId",
			},
		},
	},
})

export interface TextTransformRequest {
	transformType: string
	appId?: string
	index?: number
	language?: string
	text?: string
	testTransforms?: object
	previousText?: string
	testCVData?: Partial<App>
	promptOptionsOverride?: object
	adjustText?: string
	storeResult?: string
}

export async function getTextTransform(
	data: TextTransformRequest,
	setError: (arg0: string) => void,
	noCache = false,
	previousText?: string
) {
	const ttCache = getTTCache()

	if (data == null) return
	const secParams = await createSecurityParameters({})
	const cacheKey = JSON.stringify(data)
	const itemFromCache = noCache ? null : ttCache.getItem(cacheKey)

	if (itemFromCache != null) {
		return itemFromCache
	}

	if (previousText) {
		data.previousText = previousText
	}

	const path = "/text-transform"

	const fullPath = addIdsToPath(path, data.appId)

	try {
		const response = await API.post("ara_db_api", fullPath, {
			body: data,
			headers: secParams.headers,
		})
		if (previousText != null) {
			response.text = combinePreviousText(previousText, response.text)
		}
		ttCache.setItem(cacheKey, response)
		return response
	} catch (error) {
		if (error.response?.status === 404) {
			setError("404 not found")
		}
		if (error.response?.status === 500) {
			setError("AI Assist isn't available right now. Please try again later.")
		}
		setError("AI Assist isn't available right now. Please try again later.")
	}
}

export async function publicGetClient({ apiKey, secret, attributes }) {
	const response = await API.get?.(global.config.apiGatewayPublic.NAME, "/clients", {
		headers: {
			"x-ara-api-key": apiKey,
			"x-ara-secret": secret,
		},
		queryStringParameters: {
			attributes,
		},
	})
	return response
}

interface PublicGetTokenQSParams {
	integration: string
	type: string
	token: string
	redirectUri?: string
}

export async function publicGetToken({ apiKey, secret, integration, type, token, redirectUri }) {
	const queryStringParameters: PublicGetTokenQSParams = {
		integration,
		type,
		token,
	}
	if (redirectUri) {
		queryStringParameters.redirectUri = redirectUri
	}
	const response = await API.get?.(global.config.apiGatewayPublic.NAME, "/oauthtokens", {
		headers: {
			"x-ara-api-key": apiKey,
			"x-ara-secret": secret,
		},
		queryStringParameters,
	})
	return response
}

/** Unauthenticated: the token from an SSO invite or reset email is the credential. */
export async function redeemSsoLink(token: string): Promise<{ url: string }> {
	return API.post(global.config.apiGatewayEvo.NAME, "/evo/identity/invite", { body: { token } })
}

interface PublicFileUpload {
	b64File: string
	fileMimeType: string
}

export async function publicAddApplication({
	apiKey,
	secret,
	files,
	clientId,
	parsingData = {},
	template,
	userId,
	externalId,
	platform,
	externalClientId,
	externalJobId,
	jobId,
	folderId,
}: {
	apiKey: string
	secret: string
	files: { [des: string]: PublicFileUpload }
	clientId: string
	parsingData?: object
	template?: string
	userId?: string
	externalId?: string
	platform?: string
	externalClientId?: string
	externalJobId?: string
	jobId?: string
	folderId?: string
}) {
	const response = await API.post?.(global.config.apiGatewayPublic.NAME, "/cv", {
		headers: {
			"x-ara-api-key": apiKey,
			"x-ara-secret": secret,
		},
		body: {
			externalId,
			platform,
			externalClientId,
			externalJobId,
			jobId,
			userId,
			clientId,
			files,
			folderId,
			parseCV: true,
			parsingData: {
				main: {
					data: parsingData,
				},
			},
			persistenceData: {},
			formattingSettings: {
				defaultTemplate: template,
			},
		},
	})
	return response
}

export async function publicGetUrl({
	userId,
	externalUserId,
	apiKey,
	secret,
	externalId,
	clientId,
	platform,
	externalClientId,
	endPoint,
	extraParameters,
}: {
	userId?: string
	externalUserId?: string
	apiKey: string
	secret: string
	externalId: string
	clientId: string
	platform: string
	externalClientId: string
	endPoint?: string
	extraParameters?: object
}) {
	const response = await API.get?.(global.config.apiGatewayPublic.NAME, "/urls", {
		headers: {
			"x-ara-api-key": apiKey,
			"x-ara-secret": secret,
		},
		queryStringParameters: cleanObject(
			{
				externalId,
				clientId,
				userId,
				externalUserId,
				platform,
				externalClientId,
				endPoint,
				...extraParameters,
			},
			true
		),
	})
	return response
}

export async function publicCustomGetUrl({
	parameters,
}: {
	parameters: {
		[key: string]: string
		platform: string
		endPoint: string
	}
}) {
	const response = await API.get?.(global.config.apiGatewayCustomPublic.NAME, "/urls", {
		queryStringParameters: cleanObject(parameters, true),
	})
	return response
}

export async function getOAuthStatus(
	attributes: {
		authCode?: string
		codeVerifierId?: string
		platform?: string
		externalUserId?: string
		clientId: string
		externalClientId: string
	},
	overrideAuth?: string
) {
	const secParams =
		overrideAuth != null
			? {
					headers: {
						Authorization: overrideAuth,
						"x-access-token": "none",
					},
				}
			: await createSecurityParameters({})

	const fullPath = addIdsToPath("/oauth", attributes.clientId)
	const response = await API.get?.(global.config.apiGateway.NAME, fullPath, {
		headers: secParams.headers,
		queryStringParameters: {
			...attributes,
		},
	})
	return response
}

export async function storeOAuthPkceVerifier(clientId: string, verifier: string): Promise<{ verifierId: string }> {
	const secParams = await createSecurityParameters({})

	const fullPath = addIdsToPath("/oauth", clientId)
	const response = await API.post(global.config.apiGateway.NAME, fullPath, {
		headers: secParams.headers,
		body: { verifier },
		queryStringParameters: {},
	})
	return response
}

export async function publicGetTemplates({ apiKey, secret, clientId, attributes }) {
	const queryStringParameters: { clientId: string; attributes?: string[] } = {
		clientId,
	}
	if (attributes) {
		queryStringParameters.attributes = attributes
	}
	const response = await API.get?.(global.config.apiGatewayPublic.NAME, "/templates", {
		headers: {
			"x-ara-api-key": apiKey,
			"x-ara-secret": secret,
		},
		queryStringParameters,
	})
	return response
}

export async function publicGetJobs({ apiKey, secret, clientId, attributes }) {
	const queryStringParameters: { clientId: string; attributes?: string[] } = {
		clientId,
	}
	if (attributes != null) {
		queryStringParameters.attributes = attributes
	}
	const response = await API.get?.(global.config.apiGatewayPublic.NAME, "/job", {
		headers: {
			"x-ara-api-key": apiKey,
			"x-ara-secret": secret,
		},
		queryStringParameters,
	})
	return response
}

export async function adminUpdateClient({
	clientId,
	updateType,
	overwrite,
	text,
	expiry,
	newState,
	disabledReason,
}: {
	clientId: string
	updateType: "apiKey" | "deleteApiKey" | "addNote" | "disable"
	overwrite?: boolean
	text?: string
	expiry?: number
	newState?: boolean
	disabledReason?: string
}) {
	const secParams = await createSecurityParameters({})
	const body: {
		updateType: "apiKey" | "deleteApiKey" | "addNote" | "disable"
		overwrite?: boolean
		text?: string
		expiry?: number
		newState?: boolean
		reason?: string
	} = {
		updateType,
	}

	// Add optional parameters based on updateType
	if (overwrite !== undefined) {
		body.overwrite = overwrite
	}
	if (text !== undefined) {
		body.text = text
	}
	if (expiry !== undefined) {
		body.expiry = expiry
	}
	if (newState !== undefined) {
		body.newState = newState
	}
	if (disabledReason !== undefined) {
		body.reason = disabledReason
	}

	const fullPath = addIdsToPath("/clients", clientId)
	try {
		const record = await API.put(global.config.apiGateway.NAME, fullPath, {
			body,
			headers: secParams.headers,
		})
		return record
	} catch (error) {
		if (error?.response?.status === 404) {
			console.log("404 received - assume no clients exist")
			return {}
		}
		throw error
	}
}

export async function publicGetUser({
	apiKey,
	secret,
	clientId,
	externalUserId,
	externalClientId,
	platform,
	attributes,
}) {
	const queryStringParameters: {
		externalId: string
		externalClientId: string
		platform: string
		clientId?: string
		attributes?: string[]
	} = {
		externalId: externalUserId,
		externalClientId,
		platform,
	}
	if (clientId) {
		queryStringParameters.clientId = clientId
	}
	if (attributes) {
		queryStringParameters.attributes = attributes
	}
	const response = await API.get?.(global.config.apiGatewayPublic.NAME, "/users", {
		headers: {
			"x-ara-api-key": apiKey,
			"x-ara-secret": secret,
		},
		queryStringParameters,
	})
	return response
}

export async function publicAddUser({
	apiKey,
	secret,
	clientId,
	externalUserId,
	externalClientId,
	platform,
	email,
	name,
	phone,
	location,
	jobTitle,
}: {
	apiKey: string
	secret: string
	externalUserId: string
	externalClientId: string
	platform: string
	clientId: string
	email: string
	name: string
	phone?: string
	location?: object
	jobTitle?: string
}) {
	const body: {
		externalId: string
		externalClientId: string
		platform: string
		clientId: string
		email: string
		name: string
		phone?: string
		location?: object
	} = cleanObject({
		externalId: externalUserId,
		externalClientId,
		platform,
		clientId,
		email,
		name,
		phone,
		location,
		jobTitle,
	})

	const response = await API.post?.(global.config.apiGatewayPublic.NAME, "/users", {
		headers: {
			"x-ara-api-key": apiKey,
			"x-ara-secret": secret,
		},
		body,
	})
	return response
}

export async function publicPatchUser({
	apiKey,
	secret,
	clientId,
	externalUserId,
	externalClientId,
	platform,
	email,
	name,
	phone,
	location,
	jobTitle,
}: {
	apiKey: string
	secret: string
	externalUserId: string
	externalClientId: string
	platform: string
	clientId: string
	email: string
	name: string
	phone?: string
	location?: object
	jobTitle?: string
}) {
	const body: {
		externalId: string
		externalClientId: string
		platform: string
		clientId: string
		email: string
		name: string
		phone?: string
		location?: object
	} = cleanObject({
		externalId: externalUserId,
		externalClientId,
		platform,
		clientId,
		email,
		name,
		phone,
		location,
		jobTitle,
	})

	const response = await API.patch?.(global.config.apiGatewayPublic.NAME, "/users", {
		headers: {
			"x-ara-api-key": apiKey,
			"x-ara-secret": secret,
		},
		body,
	})
	return response
}

export async function adminGetClientUsers({
	clientId,
	userId,
	attributes,
	stats,
}: {
	clientId: string
	userId?: string
	attributes?: string[]
	stats?: string[]
}) {
	const secParams = await createSecurityParameters({})
	const params: { clientId?: string; userId?: string; attributes?: string[]; stats?: string[] } = {}
	if (attributes != null) {
		params.attributes = attributes
	}
	if (stats != null) {
		params.stats = stats
	}
	if (userId != null) {
		params.userId = userId
	}
	const response = await API.get(global.config.apiGateway.NAME, `/${clientId}/usersforclient`, {
		queryStringParameters: params,
		headers: secParams.headers,
	})
	if (userId != null) {
		return response[0]
	}
	return response
}

export async function adminGetUserInviteStatus({ clientId, userId }: { clientId: string; userId: string }) {
	const secParams = await createSecurityParameters({})
	const response = await API.get(global.config.apiGateway.NAME, `/${clientId}/userinvitestatus`, {
		queryStringParameters: { userId },
		headers: secParams.headers,
	})
	return response
}

export interface LookupUserResult {
	clientId: string
	clientName: string
	userName: string | null
	userId: string | null
	externalId: string | null
	evoRef: string | null
}

export async function adminLookupUserByEmail({ email }: { email: string }): Promise<{ results: LookupUserResult[] }> {
	const secParams = await createSecurityParameters({})
	const response = await API.get(global.config.apiGateway.NAME, "/userlookup", {
		queryStringParameters: { email },
		headers: secParams.headers,
	})
	return response
}

export async function adminDeleteClientUser({
	clientId,
	userId,
	email,
}: {
	clientId: string
	userId: string
	email?: string
}) {
	const secParams = await createSecurityParameters({})
	const response = await API.del(global.config.apiGateway.NAME, `/${clientId}/usersforclient`, {
		queryStringParameters: { userId, email },
		headers: secParams.headers,
	})
	return response
}

export async function adminAddClientUser(params: {
	clientId: string
	clientName: string
	email: string
	name: string
	userRole?: string
}) {
	const secParams = await createSecurityParameters({})

	const response = await API.post(global.config.apiGateway.NAME, `/${params.clientId}/usersforclient`, {
		queryStringParameters: params,
		headers: secParams.headers,
	})
	return response
}

export async function adminPatchClientUser({
	clientId,
	userId,
	updates,
}: {
	clientId: string
	userId: string
	updates: Partial<UserFromDb>
}) {
	const secParams = await createSecurityParameters({})
	const body = { userId, ...updates }
	const response = await API.patch(global.config.apiGateway.NAME, `/${clientId}/usersforclient`, {
		body,
		headers: secParams.headers,
	})
	return response
}

type ClientUserUpdateBody =
	| ({ clientId: string; userId: string } & BetaAccessClientUserUpdateBody)
	| ({ clientId: string; userId: string } & ResendInviteClientUserUpdateBody)
	| ({ clientId: string; userId: string } & ClearOauthTokensClientUserUpdateBody)

interface BetaAccessClientUserUpdateBody {
	updateType: "betaAccess"
	betaAccess: boolean
}
interface ResendInviteClientUserUpdateBody {
	updateType: "resendInvite"
}
interface ClearOauthTokensClientUserUpdateBody {
	updateType: "clearOauthTokens"
}

export async function adminUpdateClientUser(body: ClientUserUpdateBody) {
	const secParams = await createSecurityParameters({})

	const response = await API.put(global.config.apiGateway.NAME, `/${body.clientId}/usersforclient`, {
		body,
		headers: secParams.headers,
	})
	return response
}

export async function adminGetStats({ clientId, since, stats }: { clientId: string; since?: number; stats: string }) {
	const secParams = await createSecurityParameters({})
	const params: { since?: number; stats?: string } = {}

	if (stats != null) {
		params.stats = stats
	}
	if (since != null) {
		params.since = since
	}
	const response = await API.get(global.config.apiGateway.NAME, `/${clientId}/stats`, {
		queryStringParameters: params,
		headers: secParams.headers,
	})
	return response
}

export async function adminAddClient(body: {
	clientName: string
	clientSubdomain: string
	credits: {
		monthly: number
		annualy: number
	}
	paymentType: string
	productType: string
	limits?: {
		templates?: { limit?: number; enforced?: boolean }
		tags?: { limit?: number; enforced?: boolean }
		users?: { limit?: number; enforced?: boolean; showToUsers?: boolean }
	}
	options?: { outputs?: { [outputName: string]: CustomOutput } }
	outputData?: { [outputName: string]: { outputType: string } }
	status: string
	billingDate: string
	freeTrialEnd?: string
}) {
	const secParams = await createSecurityParameters({})

	const response = await API.post(global.config.apiGateway.NAME, "/clients", {
		body,
		headers: secParams.headers,
	})
	return response
}

export async function adminDeleteClient(body: { clientId: string; updateType: "full" | "archive" }) {
	const secParams = await createSecurityParameters({})

	try {
		const response = await API.del(global.config.apiGateway.NAME, `/${body.clientId}/clients`, {
			body,
			headers: secParams.headers,
		})
		return response
	} catch (error) {
		if (error.response?.status === 504) {
			console.log("504 received - deletion is continuing in background")
			return { response_error: "504" }
		}
		throw error
	}
}

export async function adminAddTemplate(body: { clientId: string; defaultTemplateId: string }) {
	const secParams = await createSecurityParameters({})

	const response = await API.post(global.config.apiGateway.NAME, `/${body.clientId}/templates`, {
		body,
		headers: secParams.headers,
	})
	return response
}

export async function adminGetTemplates({ clientId, attributes }: { clientId: string; attributes: string[] }) {
	const secParams = await createSecurityParameters({})

	const response = await API.get(global.config.apiGateway.NAME, `/${clientId}/templates`, {
		queryStringParameters: { attributes },
		headers: secParams.headers,
	})
	return response
}

export async function adminAddDefaultTemplate(body: {
	templateId: string
	keepHeaderImages?: boolean
	keepTagLanguage?: boolean
}) {
	const secParams = await createSecurityParameters({})

	const response = await API.post(global.config.apiGateway.NAME, "/defaulttemplates", {
		body,
		headers: secParams.headers,
	})
	return response
}

export async function adminDeleteDefaultTemplate(defaultTemplateId: string) {
	const secParams = await createSecurityParameters({})

	const response = await API.del(global.config.apiGateway.NAME, "/defaulttemplates", {
		queryStringParameters: { defaultTemplateId },
		headers: secParams.headers,
	})
	return response
}

export async function adminGetDefaultTemplates(queryStringParameters?: { attributes?: string[] }) {
	const secParams = await createSecurityParameters({})

	const response = await API.get(global.config.apiGateway.NAME, "/defaulttemplates", {
		queryStringParameters,
		headers: secParams.headers,
	})
	return response
}

// Migration API functions

/**
 * Initiate a migration job for a client from another account
 * @param body - Migration request parameters
 * @returns Promise resolving to migration job details including JWT token
 * @example
 * ```typescript
 * const result = await adminInitiateMigration({
 *   clientId: "client-123",
 *   migration_type: "full",
 *   source_account: "123456789012",
 *   scopes: ["status", "role"]
 * });
 * console.log(result.job_id); // Migration job ID
 * console.log(result.jwt_token); // Token for status checks
 * ```
 */
export async function adminInitiateMigration(body: MigrationInitiateRequest): Promise<MigrationInitiateResponse> {
	const secParams = await createSecurityParameters({})

	const requestBody = {
		migration_type: body.migration_type ?? "full",
		source_account: body.source_account,
		target: body.target ?? "test",
		scopes: body.scopes ?? ["status", "role"],
		additional_params: body.additional_params ?? {},
	}

	try {
		const response = await API.post(global.config.apiGateway.NAME, `/${body.clientId}/migration/initiate`, {
			body: requestBody,
			headers: secParams.headers,
		})
		return response
	} catch (error) {
		console.error("Migration initiation failed:", error)
		throw error
	}
}

/**
 * Get the status of a running migration job
 * @param params - Client ID and job ID to check status for
 * @returns Promise resolving to migration status and progress information
 * @example
 * ```typescript
 * const status = await adminGetMigrationStatus({
 *   clientId: "client-123",
 *   jobId: "job-456"
 * });
 * console.log(status.status.data_migration); // "pending", "in_progress", "completed", or "failed"
 * console.log(status.progress?.data_records_migrated); // Number of records migrated
 * ```
 */
export async function adminGetMigrationStatus(params: MigrationStatusRequest): Promise<MigrationStatusResponse> {
	const secParams = await createSecurityParameters({})

	const splitJobId = params.jobId.split("_")

	try {
		const response = await API.get(
			global.config.apiGateway.NAME,
			`/${splitJobId[0]}/migration/status/${splitJobId[1]}`,
			{
				headers: secParams.headers,
			}
		)
		return response
	} catch (error) {
		console.error("Migration status retrieval failed:", error)
		throw error
	}
}

/**
 * Get all migration jobs for a client
 * @param params - Client ID to get migration jobs for
 * @returns Promise resolving to array of migration jobs
 * @example
 * ```typescript
 * const jobs = await adminGetMigrationJobs({
 *   clientId: "client-123"
 * });
 * console.log(jobs.length); // Number of migration jobs
 * console.log(jobs[0]?.jobId); // Latest job ID
 * ```
 */
export async function adminGetMigrationJobs(params: MigrationJobsRequest): Promise<MigrationJobsResponse> {
	const secParams = await createSecurityParameters({})

	try {
		const response = await API.get(global.config.apiGateway.NAME, `/${params.clientId}/migration/jobs`, {
			headers: secParams.headers,
		})
		return response
	} catch (error) {
		console.error("Migration jobs retrieval failed:", error)
		throw error
	}
}

export async function forwardWithSecurity(body: {
	clientId: string
	externalClientId?: string
	externalUserId?: string
	url?: string
	platform: string
	method: Method
	data?: Blob
	json?: object
	queryStringParameters?: { [param: string]: string }
	headers?: { [header: string]: string }
	files?: any
	timeout?: number
	returnType?: "json" | "text" | "blob" | "base64"
}) {
	const secParams = await createSecurityParameters({})

	const fullPath = addIdsToPath("/forwarding", body.clientId)

	const response = await API.post(global.config.apiGateway.NAME, fullPath, {
		body,
		headers: secParams.headers,
	})
	return response
}

export async function integrationListCVList(data: IntegrationData, clientId: string): Promise<FileListObject[]> {
	const secParams = await createSecurityParameters({})

	const fullPath = addIdsToPath(`/integrations/cvfiles`, clientId)

	const response = await API.post(global.config.apiGateway.NAME, fullPath, {
		headers: secParams.headers,
		body: data,
		queryStringParameters: {},
	})

	response.forEach((element) => {
		element.dateCreated = new Date(element.dateCreated)
	})

	return response
}

export async function trackTelemetry(telemetryEvents: TelemetryEvent[], clientId: string) {
	const secParams = await createSecurityParameters({})

	const fullPath = addIdsToPath(`/telemetry/track-server`, clientId)

	const response = await API.post(global.config.apiGateway.NAME, fullPath, {
		body: telemetryEvents,
		headers: secParams.headers,
	})
	return response
}

export async function trackPlanhat(
	planhatData: {
		userId?: string
		email: string
		name: string
		action: string
		info?: any
		metricData?: Record<string, number>
	},
	clientId: string
) {
	const secParams = await createSecurityParameters({})

	const fullPath = addIdsToPath(`/tracking/planhat`, clientId)

	const response = await API.post(global.config.apiGateway.NAME, fullPath, {
		body: planhatData,
		headers: secParams.headers,
	})
	return response
}

export async function integrationGetCVContent(
	integrationData: IntegrationData,
	fileFetchingDetails: FileFetchingDetails,
	clientId: string
): Promise<FetchedFileObject> {
	const secParams = await createSecurityParameters({})

	const fullPath = addIdsToPath(`/integrations/cvfile`, clientId)

	const response = await API.post(global.config.apiGateway.NAME, fullPath, {
		headers: secParams.headers,
		body: {
			fileFetchingDetails: fileFetchingDetails,
			integrationData: integrationData,
		},
	})

	return response
}

export async function integrationGetUserDetails(
	data: IntegrationData,
	clientId: string
): Promise<{ name: string; email: string; phoneNumber?: string }> {
	const secParams = await createSecurityParameters({})

	const fullPath = addIdsToPath(`/integrations/userdetails`, clientId)

	const response = await API.post(global.config.apiGateway.NAME, fullPath, {
		headers: secParams.headers,
		body: data,
		queryStringParameters: {},
	})

	return response
}

export async function integrationSetupAddIntegration(
	data: Pick<IntegrationData, "platform">,
	clientId: string
): Promise<{ name: string; email: string; phoneNumber?: string }> {
	const secParams = await createSecurityParameters({})

	const fullPath = addIdsToPath(`/integrations/setup`, clientId)

	const response = await API.post(global.config.apiGateway.NAME, fullPath, {
		headers: secParams.headers,
		body: data,
		queryStringParameters: {},
	})

	return response
}

export async function integrationSetupDeleteIntegration(
	data: Pick<IntegrationData, "platform">,
	clientId: string
): Promise<{ name: string; email: string; phoneNumber?: string }> {
	const secParams = await createSecurityParameters({})

	const fullPath = addIdsToPath(`/integrations/setup`, clientId)

	const response = await API.del(global.config.apiGateway.NAME, fullPath, {
		headers: secParams.headers,
		body: data,
		queryStringParameters: {},
	})

	return response
}

export async function integrationSetupGetIntegration(
	data: Pick<IntegrationData, "platform">,
	clientId: string
): Promise<any[]> {
	const secParams = await createSecurityParameters({})

	const fullPath = addIdsToPath(`/integrations/setup`, clientId)

	const response = await API.get(global.config.apiGateway.NAME, fullPath, {
		headers: secParams.headers,
		queryStringParameters: { platform: data.platform },
	})

	return response
}

export async function integrationGetFieldMappings(platform: string, clientId: string): Promise<any> {
	const secParams = await createSecurityParameters({})

	const fullPath = addIdsToPath(`/integrations/fieldmappings`, clientId)

	const response = await API.get(global.config.apiGateway.NAME, fullPath, {
		headers: secParams.headers,
		queryStringParameters: { platform: platform },
	})

	return response
}

export async function getUserPhoto({ clientId, userId }: { clientId: string; userId: string }): Promise<string> {
	const secParams = await createSecurityParameters({})

	const fullPath = addIdsToPath("/user_photo", clientId)

	try {
		const response = await API.get(global.config.apiGateway.NAME, fullPath, {
			headers: secParams.headers,
			queryStringParameters: { user_id: userId },
		})
		return response
	} catch (error) {
		if (error.response?.status === 404) {
			console.log("404 received - assume no user photo exists")
			return null
		}
		throw error
	}
}

export async function putUserPhoto({
	clientId,
	userId,
	fileB64,
}: {
	clientId: string
	userId: string
	fileB64: string
}): Promise<{ message: string; filename: string }> {
	const secParams = await createSecurityParameters({})

	const fullPath = addIdsToPath("/user_photo", clientId)

	const strippedB64 = stripB64Extra(fileB64)

	const response = await API.put(global.config.apiGateway.NAME, fullPath, {
		headers: secParams.headers,
		queryStringParameters: { user_id: userId },
		body: strippedB64,
	})

	return response
}

// BrandFetch API Types - Minimal response matching backend
export interface BrandFetchLogo {
	id: string
	url: string
	format: string
}

export interface BrandFetchFont {
	name: string
	type: string // "heading" or "body"
}

export interface BrandFetchData {
	domain: string
	name?: string
	colors: string[] // All colors as hex strings
	social_links: Record<string, string> // platform: url
	logos: BrandFetchLogo[] // Up to 8 logos
	fonts: BrandFetchFont[] // All fonts with type info
}

export interface BrandFetchResponse {
	success: boolean // Always true on successful response (errors throw exceptions)
	data: BrandFetchData
	message: string
	response_time?: number
}

export interface UploadBrandLogoResponse {
	success: boolean
	message: string
	data?: {
		filename: string
		set_as_current: boolean
		file_size: number
	}
	filename?: string
	url?: string
}

// BrandFetch API Functions
export async function fetchBrandData({
	domain,
	clientId,
}: {
	domain: string
	clientId: string
}): Promise<BrandFetchResponse> {
	const secParams = await createSecurityParameters({})
	const path = "/fetch_brand_data"
	const fullPath = addIdsToPath(path, clientId)

	const queryStringParameters = {
		domain: encodeURIComponent(domain.trim()),
	}

	try {
		const response = await API.get(global.config.apiGateway.NAME, fullPath, {
			headers: secParams.headers,
			queryStringParameters,
		})
		return response
	} catch (error) {
		console.log("Error fetching brand data:", error)
		throw error
	}
}

export async function uploadBrandFetchLogo({
	logoData,
	clientId,
	filename,
}: {
	logoData: string | File
	clientId: string
	filename?: string
}): Promise<UploadBrandLogoResponse> {
	const secParams = await createSecurityParameters({})
	const path = "/logos/upload_brandfetch_logo"
	const fullPath = addIdsToPath(path, clientId)

	let body: { logo_url?: string; logo_data?: string; filename?: string; set_as_current?: boolean }

	if (logoData instanceof File) {
		// User uploaded file - convert to base64
		const fullBase64 = await getBase64(logoData)
		const processedData = stripB64Extra(fullBase64)
		const detectedFilename = filename || logoData.name

		body = {
			logo_data: processedData,
			filename: detectedFilename,
			set_as_current: true,
		}
	} else if (typeof logoData === "string" && (logoData.startsWith("http://") || logoData.startsWith("https://"))) {
		// BrandFetch URL - send directly to Lambda for download
		body = {
			logo_url: logoData,
			set_as_current: true,
		}
	} else if (typeof logoData === "string") {
		// Base64 data - strip extra and send
		const processedData = stripB64Extra(logoData)
		body = {
			logo_data: processedData,
			filename: filename,
			set_as_current: true,
		}
	} else {
		throw new Error("Invalid logo data format")
	}

	try {
		const response = await API.post(global.config.apiGateway.NAME, fullPath, {
			headers: {
				"Content-Type": "application/json",
				...secParams.headers,
			},
			body,
		})
		return response
	} catch (error) {
		console.log("Error uploading logo:", error)
		throw error
	}
}
